import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock, PlayCircle, ShieldCheck } from "lucide-react";
import MuxPlayer from "@mux/mux-player-react";
import { api, formatPrice, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_THUMB =
  "https://images.unsplash.com/photo-1478720568477-152d9b164e26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function VideoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [buying, setBuying] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    if (user === undefined) return;
    api.get(`/videos/${id}`).then((r) => setVideo(r.data)).catch(() => toast.error("Vidéo introuvable"));
  }, [id, user]);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode.trim(), video_id: id });
      setPromoInfo(data);
      toast.success(`Code ${data.code} appliqué`);
    } catch (err) {
      setPromoInfo(null);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  const launchCheckout = async (code) => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        video_id: id,
        origin_url: window.location.origin,
        promo_code: code || undefined,
      });
      if (data.free) {
        toast.success("Vidéo débloquée gratuitement !");
        const r = await api.get(`/videos/${id}`);
        setVideo(r.data);
        setBuying(false);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBuying(false);
    }
  };

  const buyNow = () => {
    if (!user) {
      sessionStorage.setItem("pending_checkout", JSON.stringify({ video_id: id, promo: promoInfo?.code || null }));
      toast.info("Créez un compte ou connectez-vous pour finaliser votre achat");
      navigate("/inscription", { state: { from: `/video/${id}` } });
      return;
    }
    launchCheckout(promoInfo?.code);
  };

  useEffect(() => {
    if (!user || !video || video.has_purchased) return;
    const raw = sessionStorage.getItem("pending_checkout");
    if (!raw) return;
    try {
      const pending = JSON.parse(raw);
      if (pending.video_id !== id) return;
      sessionStorage.removeItem("pending_checkout");
      toast.info("Redirection vers le paiement sécurisé…");
      launchCheckout(pending.promo);
    } catch {
      sessionStorage.removeItem("pending_checkout");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, video, id]);

  if (!video) {
    return (
      <div className="min-h-screen bg-luxe flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="bg-luxe min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36 pb-16">
        <button
          data-testid="back-button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-[#8F8F8F] hover:text-[#D4AF37] transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au catalogue
        </button>

        {video.has_purchased && video.mux_playback_id ? (
          <div data-testid="video-player-section">
            <MuxPlayer
              data-testid="mux-player"
              playbackId={video.mux_playback_id}
              {...(video.mux_token ? { tokens: { playback: video.mux_token } } : {})}
              metadata={{
                video_id: video.id,
                video_title: video.title,
                viewer_user_id: user?.id || "",
              }}
              accentColor="#D4AF37"
              style={{ width: "100%", aspectRatio: "16 / 9", backgroundColor: "#000000" }}
              className="border border-white/10"
            />
            <p className="mt-4 flex items-center gap-2 text-sm text-[#B0B0B0]">
              <PlayCircle className="w-4 h-4 text-[#D4AF37]" /> Vous avez accès à cette vidéo — bon visionnage.
            </p>
          </div>
        ) : video.has_purchased && video.mux_unavailable ? (
          <div data-testid="playback-unavailable" className="aspect-video bg-black border border-white/10 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <Lock className="w-10 h-10 text-[#D4AF37]" />
            <p className="text-sm text-[#B0B0B0]">
              Votre achat est confirmé, mais la lecture sécurisée est temporairement indisponible.
              Réessayez dans quelques instants.
            </p>
          </div>
        ) : video.has_purchased && video.vimeo_video_id ? (
          <div data-testid="video-player-section">
            <div className="aspect-video bg-black border border-white/10">
              <iframe
                data-testid="vimeo-player"
                src={`https://player.vimeo.com/video/${video.vimeo_video_id}`}
                title={video.title}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-[#B0B0B0]">
              <PlayCircle className="w-4 h-4 text-[#D4AF37]" /> Vous avez accès à cette vidéo — bon visionnage.
            </p>
          </div>
        ) : (
          <div className="aspect-video overflow-hidden border border-white/10 relative">
            <img src={video.thumbnail_url || DEFAULT_THUMB} alt={video.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <Lock className="w-12 h-12 text-white/90" />
            </div>
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h1 className="font-display text-3xl lg:text-4xl tracking-tight text-[#F5F3EE] leading-tight">
              {video.title}
            </h1>
            <p className="mt-6 text-[#B0B0B0] leading-relaxed whitespace-pre-line">
              {video.long_description || video.short_description}
            </p>
          </div>

          {!video.has_purchased && (
            <div className="lg:col-span-5">
              <div className="bg-[#141414] border border-white/10 p-8">
                <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-6">
                  <span data-testid="video-detail-price" className="font-display text-4xl text-[#D4AF37]">
                    {promoInfo ? (
                      <>
                        <span className="text-xl text-[#8F8F8F] line-through mr-3">
                          {formatPrice(promoInfo.original_price, video.currency)}
                        </span>
                        {formatPrice(promoInfo.final_price, video.currency)}
                      </>
                    ) : (
                      formatPrice(video.price, video.currency)
                    )}
                  </span>
                  <span className="text-sm text-[#8F8F8F]">Accès à vie</span>
                </div>
                <div className="mb-4">
                  <label className="text-xs uppercase tracking-widest text-[#8F8F8F]">Code promo (optionnel)</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      data-testid="promo-code-input"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoInfo(null); }}
                      placeholder="MONCODE"
                      className="flex-1 h-10 bg-transparent border border-white/15 px-3 text-sm text-[#F5F3EE] placeholder:text-[#5A5A5A] focus:outline-none focus:border-[#D4AF37] uppercase"
                    />
                    <button
                      data-testid="apply-promo-button"
                      onClick={applyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="border border-[#D4AF37] text-[#D4AF37] px-4 text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors disabled:opacity-40"
                    >
                      {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
                    </button>
                  </div>
                  {promoInfo && (
                    <p data-testid="promo-applied" className="mt-2 text-xs text-[#D4AF37]">
                      Code {promoInfo.code} appliqué : −
                      {promoInfo.discount_type === "percent" ? `${promoInfo.value}%` : formatPrice(promoInfo.value, video.currency)}
                    </p>
                  )}
                </div>
                {!user && (
                  <p data-testid="account-required-note" className="mb-4 text-xs text-[#8F8F8F] leading-relaxed border border-white/10 p-3">
                    Un compte est requis pour acheter — vous serez invité à vous inscrire ou vous
                    connecter, puis redirigé automatiquement vers le paiement.
                  </p>
                )}
                <button
                  data-testid="buy-button"
                  onClick={buyNow}
                  disabled={buying}
                  className="w-full bg-[#D4AF37] text-[#0A0A0A] font-semibold py-4 text-center text-sm uppercase tracking-widest hover:bg-[#C9A227] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {buying && <Loader2 className="w-4 h-4 animate-spin" />} Acheter
                </button>
                <p className="mt-5 text-xs text-[#8F8F8F] leading-relaxed">
                  {user ? (
                    <>
                      L'achat sera lié à votre compte <strong>{user.email}</strong> — votre vidéo sera
                      débloquée immédiatement après le paiement.
                    </>
                  ) : (
                    <>
                      Déjà un compte ?{" "}
                      <Link to="/connexion" state={{ from: `/video/${id}` }} className="underline text-[#D4AF37]" data-testid="login-hint-link">
                        Connectez-vous
                      </Link>{" "}
                      avant d'acheter.
                    </>
                  )}
                </p>
                <p className="mt-4 flex items-center gap-2 text-xs text-[#8F8F8F]">
                  <ShieldCheck className="w-3.5 h-3.5" /> Paiement 100% sécurisé par carte via Stripe
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
